import { useRef } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import AttachFile from '@mui/icons-material/AttachFile';
import { MAX_ADJUNTOS, validarArchivo } from '../hooks/useTramites';
import { formatBytes } from '../utils/format';

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  /** Mensaje de validacion para mostrar debajo (lo calcula quien usa el componente). */
  error?: string | null;
  onError: (msg: string | null) => void;
  disabled?: boolean;
}

/** Selector de adjuntos (PDF, PNG o JPG, hasta 5 archivos de 10 MB). */
export default function AdjuntosInput({ files, onChange, error, onError, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = (list: FileList | null) => {
    if (!list) return;
    const nuevos = Array.from(list);
    for (const f of nuevos) {
      const msg = validarArchivo(f);
      if (msg) return onError(msg);
    }
    if (files.length + nuevos.length > MAX_ADJUNTOS) return onError(`Podés adjuntar hasta ${MAX_ADJUNTOS} archivos.`);
    onError(null);
    onChange([...files, ...nuevos]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="outlined" startIcon={<AttachFile />} onClick={() => inputRef.current?.click()} disabled={disabled}>
          Adjuntar archivos
        </Button>
        <Typography variant="body2" color="text.secondary">
          PDF, PNG o JPG · hasta {MAX_ADJUNTOS} archivos de 10 MB
        </Typography>
      </Stack>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        accept="application/pdf,image/png,image/jpeg"
        aria-label="Adjuntar archivos"
        onChange={(e) => handlePick(e.target.files)}
      />
      {files.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {files.map((f, i) => (
            <Chip
              key={`${f.name}-${i}`}
              label={`${f.name} (${formatBytes(f.size)})`}
              onDelete={disabled ? undefined : () => onChange(files.filter((_, j) => j !== i))}
            />
          ))}
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
